import AssetService from './asset.service';
import BuilderService from './builder.service';
// import grapesjsmautic from 'grapesjs-preset-mautic/src/content.service';

// all css get combined into one builder.css and automatically loaded via js/parcel
import 'grapesjs/dist/css/grapes.min.css';
// not compatible with the newsletter preset css, brings the redish color
// import 'grapesjs-preset-webpage/dist/grapesjs-preset-webpage.min.css';
import 'grapesjs-preset-newsletter/dist/grapesjs-preset-newsletter.css';
import './grapesjs-custom.css';
import '../css/enhanced-code-editor.css';
import '../css/grapesjs-ux-enhancements.css';

/**
 * Canonical "blank email" document: a minimal, valid, responsive HTML email with an
 * empty editable body (no legacy {subject}/{content} tokens). Used both as the
 * runtime fallback when an email has no usable content and as the payload for the
 * "Blank" theme card, so a blank start looks identical however it's reached.
 */
const BLANK_EMAIL_HTML = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>{subject}</title>
<style type="text/css">
  body { margin: 0; padding: 0; background-color: #f4f4f4; font-family: Arial, Helvetica, sans-serif; }
  .email-body { max-width: 600px; margin: 0 auto; background-color: #ffffff; }
</style>
</head>
<body>
<div class="email-body" style="max-width:600px;margin:0 auto;background-color:#ffffff;padding:20px;"></div>
</body>
</html>`;

/**
 * True when the builder-html textarea holds a parseable document with non-empty
 * head AND body — the same condition the preset's ContentService requires before
 * it will render (otherwise it throws "No valid HTML template found").
 */
function emailHtmlIsUsable() {
  const html = mQuery('textarea.builder-html').val() || '';
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return !!(doc.body && doc.body.innerHTML.trim() && doc.head && doc.head.innerHTML.trim());
  } catch (e) {
    return false;
  }
}

/**
 * Load the blank email document into the builder-html textarea and clear any MJML,
 * so initGrapesJS takes the email-html (newsletter) path with a clean empty canvas.
 */
function seedBlankEmailHtml() {
  const textareaMjml = mQuery('textarea.builder-mjml');
  if (textareaMjml.length) {
    textareaMjml.val('');
  }
  mQuery('textarea.builder-html').val(BLANK_EMAIL_HTML);
}

/**
 * Ensure an email has something usable to open in the builder. MJML content is left
 * untouched (the MJML path handles its own emptiness); otherwise, if the HTML is
 * empty/headless, seed a blank document so the builder never opens broken.
 */
function ensureEmailBuilderContent() {
  const mjml = mQuery('textarea.builder-mjml').val() || '';
  if (mjml.trim()) {
    return;
  }
  if (!emailHtmlIsUsable()) {
    seedBlankEmailHtml();
  }
}

/**
 * Launch builder
 *
 * @param formName
 */
function launchBuilderGrapesjs(formName) {
  if (useBuilderForCodeMode() === false) {
    return;
  }

  Mautic.showChangeThemeWarning = true;

  // Prepare HTML
  mQuery('html').css('font-size', '100%');
  mQuery('body').css('overflow-y', 'hidden');
  mQuery('.builder-panel').css('padding', 0);
  mQuery('.builder-panel').css('display', 'block');
  mQuery('.builder').addClass('builder-active').removeClass('hide');

  // An email must always open into a usable editor. If it has no usable content,
  // seed a blank document so GrapesJS renders a clean canvas instead of throwing
  // and leaving the legacy builder shell showing.
  if (formName === 'emailform') {
    ensureEmailBuilderContent();
  }

  const assetsConfig = AssetService.getAssetsConfig();
  const builder = new BuilderService(assetsConfig);

  // Initialize GrapesJS. Guard against any content-parse failure so the broken,
  // half-rendered legacy shell is never what the user is left looking at.
  try {
    builder.initGrapesJS(formName);
  } catch (initError) {
    console.error('GrapesJS failed to initialize; retrying with a blank email.', initError);
    if (formName !== 'emailform') {
      closeBrokenBuilder();
      throw initError;
    }
    seedBlankEmailHtml();
    try {
      builder.initGrapesJS(formName);
    } catch (retryError) {
      console.error('GrapesJS still failed after the blank fallback.', retryError);
      closeBrokenBuilder();
      return;
    }
  }

  // Store builder globally so other pages (e.g., template editor) can access the editor
  Mautic.builderInstance = builder;

  // Load and add assets
  AssetService.getAssetsXhr(function (result) {
    builder.editor.AssetManager.add(result.data);
  });
}

/**
 * Hide the half-rendered builder (legacy shell + panel) and restore page scroll, so
 * a failed launch returns the user to the form rather than a broken overlay.
 */
function closeBrokenBuilder() {
  mQuery('.builder').addClass('hide').removeClass('builder-active');
  mQuery('.builder-panel').css('display', 'none');
  mQuery('body').css('overflow-y', '');
}

/**
 * The user acknowledges the risk before editing an email or landing page created in Code Mode in the Builder
 */
function useBuilderForCodeMode() {
  const theme = mQuery('.theme-selected').find('[data-theme]').attr('data-theme');
  const isCodeMode = theme === 'mautic_code_mode';

  if (isCodeMode) {
    if (confirm(Mautic.translate('grapesjsbuilder.builder.warning.code_mode')) === false) {
      return false;
    }
  }

  return true;
}

/**
 * Set theme's HTML
 *
 * @param theme
 */
function setThemeHtml(theme) {
  // "Blank" is a synthetic theme (not a server template): start from a clean, empty
  // email document — no AJAX round-trip, no legacy {content} token.
  if (theme === 'blank') {
    seedBlankEmailHtml();
    return;
  }

  BuilderService.setupButtonLoadingIndicator(true);
  // Load template and fill field
  mQuery.ajax({
    url: mQuery('#builder_url').val(),
    data: `template=${theme}`,
    dataType: 'json',
    success(response) {
      const textareaHtml = mQuery('textarea.builder-html');
      const textareaMjml = mQuery('textarea.builder-mjml');

      textareaHtml.val(response.templateHtml);

      if (typeof textareaMjml !== 'undefined') {
        textareaMjml.val(response.templateMjml);
      }

      // If MJML template, generate HTML before save
      // if (!textareaHtml.val().length && textareaMjml.val().length) {
      //   builder.mjmlToHtml(textareaMjml, textareaHtml);
      // }
      // }
    },
    error(request, textStatus) {
      console.error(`setThemeHtml - Request failed: ${textStatus}`);
    },
    complete() {
      BuilderService.setupButtonLoadingIndicator(false);
    },
  });
}

/**
 * The builder button to launch GrapesJS will be disabled when the code mode theme is selected
 *
 * @param theme
 */
function switchBuilderButton(theme) {
  const builderButton = mQuery('.btn-builder');
  const mEmailBuilderButton = mQuery('#emailform_buttons_builder_toolbar_mobile');
  const mPageBuilderButton = mQuery('#page_buttons_builder_toolbar_mobile');
  const isCodeMode = theme === 'mautic_code_mode';

  builderButton.attr('disabled', isCodeMode);

  if (isCodeMode) {
    mPageBuilderButton.addClass('link-is-disabled');
    mEmailBuilderButton.addClass('link-is-disabled');

    mPageBuilderButton.parent().addClass('is-not-allowed');
    mEmailBuilderButton.parent().addClass('is-not-allowed');
  } else {
    mPageBuilderButton.removeClass('link-is-disabled');
    mEmailBuilderButton.removeClass('link-is-disabled');

    mPageBuilderButton.parent().removeClass('is-not-allowed');
    mEmailBuilderButton.parent().removeClass('is-not-allowed');
  }
}

/**
 * The textarea with the HTML source will be displayed if the code mode theme is selected
 *
 * @param theme
 */
function switchCustomHtml(theme) {
  const customHtmlRow = mQuery('#custom-html-row');
  const isPageMode = mQuery('[name="page"]').length !== 0;
  const isCodeMode = theme === 'mautic_code_mode';
  const advancedTab = isPageMode ? mQuery('#advanced-tab') : null;

  if (isCodeMode === true) {
    customHtmlRow.removeClass('hidden');
    isPageMode && advancedTab.removeClass('hidden');
  } else {
    customHtmlRow.addClass('hidden');
    isPageMode && advancedTab.addClass('hidden');
  }
}

/**
 * Initialize original Mautic theme selection with grapejs specific modifications
 */
function initSelectThemeGrapesjs(parentInitSelectTheme) {
  function childInitSelectTheme(themeField) {
    const builderUrl = mQuery('#builder_url');
    let url;

    switchBuilderButton(themeField.val());
    switchCustomHtml(themeField.val());

    // Replace Mautic URL by plugin URL
    if (builderUrl.length) {
      if (builderUrl.val().indexOf('pages') !== -1) {
        url = builderUrl.val().replace('s/pages/builder', 's/grapesjsbuilder/page');
      } else {
        url = builderUrl.val().replace('s/emails/builder', 's/grapesjsbuilder/email');
      }

      builderUrl.val(url);
    }

    // Launch original Mautic.initSelectTheme function
    parentInitSelectTheme(themeField);

    // Scope to theme-select links only. A bare '[data-theme]' also matches the
    // dark-mode root <html data-theme="dark">, binding this handler to <html> so
    // it runs on every page click (event.target then has no theme → switches to a
    // bogus 'dark' theme). Mirrors the same fix in core initSelectTheme.
    mQuery('a[data-theme]').click((event) => {
      const theme = mQuery(event.target).closest('a[data-theme]').attr('data-theme');

      switchBuilderButton(theme);
      switchCustomHtml(theme);
    });
  }
  return childInitSelectTheme;
}

Mautic.launchBuilder = launchBuilderGrapesjs;
Mautic.initSelectTheme = initSelectThemeGrapesjs(Mautic.initSelectTheme);
Mautic.setThemeHtml = setThemeHtml;
